import React from 'react';
import { Flex, Box, IconButton, Badge } from '@chakra-ui/react';
import classNames from 'classnames';
import { IconLogoTypeSmall, IconNotifications } from '../../components/Icons';
import { UserMenu } from '../../components/UserMenu';
import LanguageChooser from '../../components/LanguageChooser';
import './Header.scss';

/**
 * Header View Component
 * Pure presentation component - no business logic
 *
 * DDD Layer: UI (Presentation)
 * Responsibilities:
 * - Rendering UI elements
 * - Styling
 * - Receiving props from container component
 */
interface HeaderViewProps {
  className?: string;
  showProfile?: boolean;
  showNotifications?: boolean;
  showLanguageSelector?: boolean;
  logo?: React.ReactNode | string;
  onLogoClick?: () => void;
  navigationItems?: any[];
  userProfile?: any;
  companyData?: any;
  isMenuOpen?: boolean;
  isNotificationsOpen?: boolean;
  onProfileMenuToggle?: () => void;
  onNotificationsToggle?: () => void;
  onLogout?: () => void;
  profileTo?: string;
  settingsTo?: string;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  t: (key: string) => string;
}

const HeaderView: React.FC<HeaderViewProps> = ({
  className,
  showProfile = true,
  showNotifications = true,
  showLanguageSelector = false,
  logo,
  onLogoClick,
  navigationItems,
  userProfile,
  companyData,
  isMenuOpen,
  isNotificationsOpen,
  onProfileMenuToggle,
  onNotificationsToggle,
  onLogout,
  profileTo,
  settingsTo,
  onProfileClick,
  onSettingsClick,
  t,
}) => {
  return (
    <Flex
      as='header'
      className={classNames('app-header', className)}
      align='center'
      justify='space-between'
      px={6}
      py={3}
      bg='white'
      borderBottom='1px solid'
      borderColor='gray.200'
      position='sticky'
      top={0}
      zIndex={1000}
    >
      {/* Logo Section */}
      <Flex
        align='center'
        className='app-header__logo'
        onClick={onLogoClick}
        cursor='pointer'
      >
        {typeof logo === 'string' ? (
          <img src={logo} alt='Logo' className='app-header__logo-image' />
        ) : logo ? (
          logo
        ) : (
          <IconLogoTypeSmall />
        )}
      </Flex>

      {/* Navigation Items (optional) */}
      {navigationItems && navigationItems.length > 0 && (
        <Flex className='app-header__navigation' gap={4}>
          {navigationItems.map(item => (
            <Box
              key={item.id}
              className={classNames('app-header__nav-item', {
                'app-header__nav-item--active': item.isActive,
              })}
              onClick={item.onClick}
              cursor='pointer'
              px={3}
              py={2}
              borderRadius='md'
              _hover={{ bg: 'gray.100' }}
            >
              {item.icon && (
                <span className='app-header__nav-icon'>{item.icon}</span>
              )}
              <span className='app-header__nav-label'>{item.label}</span>
            </Box>
          ))}
        </Flex>
      )}

      {/* Right Section: Notifications, Language, Profile */}
      <Flex align='center' gap={4} className='app-header__actions'>
        {/* Notifications */}
        {showNotifications && (
          <Box position='relative'>
            <IconButton
              aria-label='Notifications'
              icon={<IconNotifications />}
              variant='ghost'
              onClick={onNotificationsToggle}
              className='app-header__notification-btn'
            />
            <Badge
              position='absolute'
              top='-1'
              right='-1'
              colorScheme='red'
              borderRadius='full'
              className='app-header__notification-badge'
            >
              3
            </Badge>
          </Box>
        )}

        {/* Language Selector */}
        {showLanguageSelector && (
          <Box
            className='app-header__language'
            display='flex'
            alignItems='center'
          >
            <LanguageChooser width={170} />
          </Box>
        )}

        {/* User Profile Menu */}
        {showProfile && (
          <UserMenu
            user={userProfile}
            profileTo={profileTo}
            settingsTo={settingsTo}
            onProfileClick={onProfileClick}
            onSettingsClick={onSettingsClick}
            onLogoutClick={onLogout}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default HeaderView;
